import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Save, KeyRound, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  adminListClients, adminCreateClient, adminUpdateClient, adminDeleteClient,
} from "@/lib/api";

const EMPTY_FORM = {
  name: "", dashboard_name: "", email: "", username: "",
  password: "", finance_password: "",
  address_line1: "", address_line2: "", city_state_country: "",
  notes: "",
};

export default function Clients({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = create, row = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await adminListClients(token);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing({});
  };
  const openEdit = (row) => {
    setForm({
      name: row.name || "", dashboard_name: row.dashboard_name || "",
      email: row.email || "", username: row.username || "",
      password: row.password || "",
      finance_password: row.finance_password || "",
      address_line1: row.address_line1 || "",
      address_line2: row.address_line2 || "",
      city_state_country: row.city_state_country || "",
      notes: row.notes || "",
    });
    setEditing(row);
  };
  const closeModal = () => { setEditing(null); setForm(EMPTY_FORM); };

  const isEdit = editing && editing.id;

  const submit = async () => {
    if (!form.name.trim() || !form.username.trim()) { toast.error("Name and username are required"); return; }
    if (!isEdit && (!form.password || form.password.length < 6 || !form.finance_password || form.finance_password.length < 6)) {
      toast.error("Both passwords must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name, dashboard_name: form.dashboard_name,
          email: form.email, username: form.username,
          address_line1: form.address_line1, address_line2: form.address_line2,
          city_state_country: form.city_state_country,
          notes: form.notes,
        };
        if (form.password) payload.password = form.password;
        if (form.finance_password) payload.finance_password = form.finance_password;
        await adminUpdateClient(token, editing.id, payload);
        toast.success("Client updated");
      } else {
        await adminCreateClient(token, form);
        toast.success("Client created");
      }
      closeModal();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete client "${row.name}"? Their login will stop working immediately. Projects remain.`)) return;
    try {
      await adminDeleteClient(token, row.id);
      toast.success("Client deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const portalUrl = `${window.location.origin}/client`;
  const copyPortalUrl = (username) => {
    const url = `${portalUrl}?u=${encodeURIComponent(username)}`;
    navigator.clipboard.writeText(url);
    toast.success("Portal URL copied");
  };

  return (
    <div data-testid="admin-clients-page">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Client Portal</div>
          <h2 className="mt-1 crm-h text-3xl md:text-4xl text-[#0a0a0a]">Clients & access</h2>
          <p className="mt-2 text-sm text-[#4a4a4a]">
            Create logins for clients. They see project status by default; payments &amp; invoices require their finance password.
            Portal:{" "}
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-[#ff5722] font-mono inline-flex items-center gap-1">
              {portalUrl} <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="admin-clients-new"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] bg-[#0a0a0a] text-white rounded-full hover:bg-[#ff5722]"
        >
          <Plus className="h-4 w-4" /> New client
        </button>
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-sm text-[#4a4a4a]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-sm text-[#4a4a4a]">No clients yet. Click &quot;New client&quot; to create one.</div>
        ) : (
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-4 w-12">S.No</th>
                <th className="px-4 py-4">Client</th>
                <th className="px-4 py-4">Dashboard name</th>
                <th className="px-4 py-4">Username</th>
                <th className="px-4 py-4">Email</th>
                <th className="px-4 py-4">Finance pwd</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} data-testid={`client-row-${r.id}`} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]">
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-[#0a0a0a]">{r.name}</div>
                    {r.notes && <div className="text-[11px] text-[#4a4a4a] mt-0.5 line-clamp-1">{r.notes}</div>}
                  </td>
                  <td className="px-4 py-4 text-sm text-[#0a0a0a]">{r.dashboard_name || "—"}</td>
                  <td className="px-4 py-4">
                    <div className="inline-flex items-center gap-2">
                      <span className="font-mono text-sm">{r.username}</span>
                      <button onClick={() => copyPortalUrl(r.username)} title="Copy portal URL" className="text-[#4a4a4a] hover:text-[#ff5722]">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#4a4a4a]">{r.email || "—"}</td>
                  <td className="px-4 py-4">
                    {r.has_finance_password ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] bg-emerald-100 text-emerald-800 rounded">
                        <KeyRound className="h-3 w-3" /> Set
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] bg-rose-100 text-rose-700 rounded">Missing</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => openEdit(r)} data-testid={`client-edit-${r.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]">
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button onClick={() => remove(r)} data-testid={`client-delete-${r.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-black/15 rounded-full hover:border-red-500 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-0 md:p-6">
          <div className="bg-white w-full md:max-w-xl max-h-[90vh] overflow-auto rounded-t-2xl md:rounded-2xl border border-black/10 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/10 sticky top-0 bg-white">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#ff5722] font-bold">Client</div>
                <div className="font-bold text-[#0a0a0a] mt-0.5">{isEdit ? `Edit · ${editing.name}` : "New client"}</div>
              </div>
              <button onClick={closeModal} className="text-[#4a4a4a] hover:text-[#0a0a0a]"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <FieldRow label="Client name" required>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="EBG Group" className="input" data-testid="client-form-name" />
              </FieldRow>
              <FieldRow label="Dashboard name" help='Shown at the top of the client dashboard. e.g. "EBG Projects Dashboard"'>
                <input value={form.dashboard_name} onChange={(e) => setForm({ ...form, dashboard_name: e.target.value })} placeholder={`${form.name || "Client"} Projects Dashboard`} className="input" data-testid="client-form-dashboard-name" />
              </FieldRow>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldRow label="Username" required>
                  <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s+/g, "") })} placeholder="ebg" className="input font-mono" data-testid="client-form-username" />
                </FieldRow>
                <FieldRow label="Email">
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@client.com" className="input" data-testid="client-form-email" />
                </FieldRow>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldRow label="Login password" required={!isEdit} help={isEdit ? "Current password shown above — edit only to change." : null}>
                  <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 chars" className="input font-mono" data-testid="client-form-password" />
                </FieldRow>
                <FieldRow label="Finance password" required={!isEdit} help={isEdit ? "Current finance password shown above — edit only to change." : "Required to unlock Payments & Invoices tabs"}>
                  <input type="text" value={form.finance_password} onChange={(e) => setForm({ ...form, finance_password: e.target.value })} placeholder="At least 6 chars" className="input font-mono" data-testid="client-form-finance-password" />
                </FieldRow>
              </div>
              <div className="pt-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold mb-2">Billing address (for auto-generated receipts)</div>
                <FieldRow label="Address line 1">
                  <input type="text" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="input" placeholder="Building / Street" data-testid="client-form-addr1" />
                </FieldRow>
                <FieldRow label="Address line 2">
                  <input type="text" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} className="input" placeholder="Area / Landmark (optional)" data-testid="client-form-addr2" />
                </FieldRow>
                <FieldRow label="City, State, Country">
                  <input type="text" value={form.city_state_country} onChange={(e) => setForm({ ...form, city_state_country: e.target.value })} className="input" placeholder="e.g. Hyderabad, Telangana, India" data-testid="client-form-city" />
                </FieldRow>
              </div>
              <FieldRow label="Internal notes">
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input resize-none" placeholder="Visible only to admins" />
              </FieldRow>
            </div>
            <div className="px-5 py-4 border-t border-black/10 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={closeModal} className="px-4 py-2 text-xs border border-black/15 rounded-full">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="client-form-submit" className="inline-flex items-center gap-2 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] bg-[#0a0a0a] text-white rounded-full hover:bg-[#ff5722] disabled:opacity-60">
                <Save className="h-4 w-4" /> {busy ? "Saving…" : isEdit ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.15);border-radius:6px;padding:8px 12px;font-size:14px;outline:none}.input:focus{border-color:#ff5722;box-shadow:0 0 0 3px rgba(255,87,34,0.1)}`}</style>
    </div>
  );
}

function FieldRow({ label, required, help, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-mono uppercase tracking-[0.15em] text-[#4a4a4a] mb-1.5">
        {label}{required && <span className="text-[#ff5722]"> *</span>}
      </span>
      {children}
      {help && <span className="block mt-1 text-[11px] text-[#4a4a4a] italic">{help}</span>}
    </label>
  );
}
